import { useState, useEffect, useRef, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Dialog from '@radix-ui/react-dialog'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Camera,
  Plus,
  Search,
  MoreVertical,
  Loader2,
  ImageOff,
  Eye,
  Image,
  Video,
  BatteryFull,
  BatteryMedium,
  BatteryLow,
  BatteryWarning,
  FolderOpen,
  Users,
  Trash2,
  CheckSquare,
  Square,
  Settings,
  MapPin,
  X,
  Pencil,
  Check,
  Signal,
  Activity,
  AlertTriangle,
  Thermometer,
  HardDrive,
  Cpu,
  Maximize2,
  Lock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { IngestionTypeBadge } from '@/components/cameras/IngestionTypeBadge'
import { OnlineStatusBadge } from '@/components/cameras/OnlineStatusBadge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Tooltip } from '@/components/ui/tooltip'
import { CopyButton } from '@/components/ui/copy-button'
import { CameraEditSheet } from '@/components/cameras/CameraEditSheet'
import { CameraMapModal } from '@/components/cameras/CameraMapModal'
import { AddCameraModal } from '@/components/cameras/AddCameraModal'
import { SIMDetailModal } from '@/components/cameras/SIMDetailModal'
import type { SIMUsage } from '@/types'
import { camerasApi, cameraGroupsApi, eventsApi } from '@/api/endpoints'
import { EventDetailModal } from '@/components/events/EventDetailModal'
import { queryKeys, queryOptions } from '@/config/queryConfig'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input as TextInput } from '@/components/ui/input'
import { formatRelativeTimeShort, formatAbsoluteDateTime, formatSDCard } from '@/lib/utils'
import { useAuthStore } from '@/store/authStore'
import { toast } from 'sonner'
import type { Camera as CameraType, Event as EventType, PaginatedResponse } from '@/types'
import { useDirectionStore } from '@/store/directionStore'
import { CommandCameraBoard } from '@/pages/cameras/CommandCameraBoard'
import { SaasCameraList } from '@/pages/cameras/SaasCameraList'
import { ExpressiveCameraCards } from '@/pages/cameras/ExpressiveCameraCards'

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

// Camera thumbnail component with loading states
function CameraThumbnail({
  src,
  alt,
  totalDetections,
  totalPhotos,
  totalVideos,
  locked = false,
}: {
  src: string | null
  alt: string
  totalDetections: number
  totalPhotos: number
  totalVideos: number
  locked?: boolean
}) {
  const { t } = useTranslation('cameras')
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>(
    src ? 'loading' : 'error'
  )

  // #077 — locked camera: show a lock placeholder, never the captured image or counts.
  if (locked) {
    return (
      <div className="relative h-48 bg-muted overflow-hidden flex flex-col items-center justify-center gap-1.5">
        <Lock className="h-8 w-8 text-muted-foreground/30" />
        <span className="text-xs text-muted-foreground/50">{t('card.locked', { defaultValue: 'Locked' })}</span>
      </div>
    )
  }

  return (
    <div className="relative h-48 bg-muted overflow-hidden">
      {src && imageStatus !== 'error' ? (
        <>
          {imageStatus === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/40" />
            </div>
          )}
          <img
            key={src}
            src={src}
            alt={alt}
            className={`h-full w-full object-cover transition-opacity duration-300 ${
              imageStatus === 'loaded' ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageStatus('loaded')}
            onError={() => setImageStatus('error')}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
          {src === null ? (
            <>
              <Camera className="h-8 w-8 text-muted-foreground/25" />
              <span className="text-xs text-muted-foreground/40">{t('card.noDetectionsYet')}</span>
            </>
          ) : (
            <>
              <ImageOff className="h-8 w-8 text-muted-foreground/25" />
              <span className="text-xs text-muted-foreground/40">{t('card.imageUnavailable')}</span>
            </>
          )}
        </div>
      )}

      {/* Media counts overlay — photos / videos always; detections only when
          >0. Bottom-left, matching the theme of the other thumbnail pills.
          Tooltip carries the labeled counts relocated from the old text row. */}
      <div className="absolute left-2 bottom-2 z-10">
        <Tooltip content={`${t('stats.photos', { count: totalPhotos })} · ${t('stats.videos', { count: totalVideos })}`}>
          <div className="flex items-center gap-1.5 rounded-full bg-black/50 px-2 py-0.5 text-[10px] text-white backdrop-blur-sm shadow-sm">
            <span className="flex items-center gap-1"><Image className="h-2.5 w-2.5" />{totalPhotos}</span>
            <span className="flex items-center gap-1"><Video className="h-2.5 w-2.5" />{totalVideos}</span>
            {totalDetections > 0 && (
              <span className="flex items-center gap-1"><Eye className="h-2.5 w-2.5" />{totalDetections}</span>
            )}
          </div>
        </Tooltip>
      </div>
    </div>
  )
}

// Battery indicator component
function BatteryIndicator({ level }: { level: number | null }) {
  if (level === null) {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground/50">
        <BatteryWarning className="h-4 w-4" />
        <span className="text-sm font-medium">—</span>
      </span>
    )
  }

  // Battery is actionable — strong visible signal at "good" so operators can
  // see at a glance which cameras are healthy; bold warning/danger so low
  // battery is impossible to miss (client requirement: replace battery before
  // camera dies).
  //   >60%:  BatteryFull   + green  (success)
  // 25-60%:  BatteryMedium  + foreground (neutral, no warning earned yet)
  // 10-25%:  BatteryLow     + amber  (warning — pair with thumbnail pill)
  //   ≤10%:  BatteryWarning + red   (danger — pair with thumbnail pill)
  let Icon = BatteryFull
  let colorClass = 'text-green-500'

  if (level <= 10) {
    Icon = BatteryWarning
    colorClass = 'text-red-500'
  } else if (level <= 25) {
    Icon = BatteryLow
    colorClass = 'text-amber-500'
  } else if (level <= 60) {
    Icon = BatteryMedium
    colorClass = ''  // medium — no color, just the icon change
  }

  return (
    <span className={`flex items-center gap-1.5 ${colorClass}`}>
      <Icon className="h-4 w-4" />
      <span className="text-sm font-medium">{level}%</span>
    </span>
  )
}

// 5-bar cell-phone-style signal visualization. Bars are increasing height,
// active bars 1..level use full opacity, remaining bars are dimmed (opacity-25).
// Color follows the same convention as BatteryIndicator (green=strong, red=weak).
function SignalBars({ level, max = 5, className = '' }: { level: number; max?: number; className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" className={`shrink-0 ${className}`}>
      {Array.from({ length: max }, (_, i) => i + 1).map((bar) => {
        const x = (bar - 1) * 2.6
        const height = bar * 2 + 2
        const y = 14 - height
        const isOn = bar <= level
        return (
          <rect
            key={bar}
            x={x}
            y={y}
            width="1.8"
            height={height}
            rx="0.3"
            className="fill-current"
            opacity={isOn ? 1 : 0.25}
          />
        )
      })}
    </svg>
  )
}

// Signal indicator — informational (user can't fix bad signal). 4-5 bars green
// (consistent with Battery's "good = green" convention), 3 neutral, 2 amber,
// ≤1 red. Null level → muted "—".
function SignalIndicator({ level, max = 5 }: { level: number | null; max?: number }) {
  if (level === null) {
    return (
      <span className="flex items-center gap-1.5 text-muted-foreground/50">
        <SignalBars level={0} max={max} />
        <span className="text-sm font-medium">—</span>
      </span>
    )
  }

  let colorClass = 'text-green-500'
  if (level <= 1) colorClass = 'text-red-500'
  else if (level <= 2) colorClass = 'text-amber-500'
  else if (level <= 3) colorClass = ''  // 3 bars: neutral foreground

  return (
    <span className={`flex items-center gap-1.5 ${colorClass}`}>
      <SignalBars level={level} max={max} />
      <span className="text-sm font-medium">{level}/{max}</span>
    </span>
  )
}

// Thumbnail-corner alert pill that appears when battery enters warning (≤25%)
// or critical (≤10%) range. Highly visible — translucent dark + warning icon —
// so operators see at-a-glance which cameras need a battery swap. Returns null
// when battery is fine.
function BatteryAlertPill({ level }: { level: number | null }) {
  const { t } = useTranslation('cameras')
  if (level === null || level > 25) return null

  const isCritical = level <= 10
  const toneClasses = isCritical
    ? 'bg-red-500/85 text-white border-red-300/40 backdrop-blur-sm shadow-sm'
    : 'bg-amber-500/85 text-white border-amber-300/40 backdrop-blur-sm shadow-sm'

  return (
    <Tooltip content={isCritical ? t('card.batteryCriticalTooltip') : t('card.batteryLowTooltip')}>
      <span
        className={`inline-flex items-center justify-center rounded-full p-1.5 border shrink-0 ${toneClasses}`}
        aria-label={isCritical ? t('card.batteryCriticalTooltip') : t('card.batteryLowTooltip')}
      >
        <AlertTriangle className="h-4 w-4" />
      </span>
    </Tooltip>
  )
}

// Camera card component
const CameraCard = memo(function CameraCard({
  camera,
  selected,
  selectionMode,
  onToggleSelect,
  onDeleteRequest,
  onEditRequest,
  onMapRequest,
  onSimRequest,
  onThumbnailClick,
  simUsageMB,
}: {
  camera: CameraType
  selected: boolean
  selectionMode: boolean
  onToggleSelect: (id: number) => void
  onDeleteRequest: (camera: CameraType) => void
  onEditRequest: (camera: CameraType) => void
  onMapRequest: (camera: CameraType) => void
  onSimRequest: (camera: CameraType) => void
  onThumbnailClick: (camera: CameraType) => void
  simUsageMB?: number | null
}) {
  const navigate = useNavigate()
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)  // #076 — SIM mismatch badge is superuser-only

  const handleViewEvents = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (camera.is_locked) {  // #077 — SIM mismatch: locked
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    navigate(`/events?camera=${camera.id}`)
  }

  const handleCardClick = () => {
    if (selectionMode) { onToggleSelect(camera.id); return }
    if (camera.is_locked) {  // #077 — SIM mismatch: locked until a superuser resolves it
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    navigate(`/events?camera=${camera.id}`)
  }

  return (
    <Card
      className={`overflow-hidden transition-all duration-150 flex flex-col group relative ${camera.is_locked ? 'cursor-not-allowed opacity-75' : 'cursor-pointer hover:ring-1 hover:ring-primary/30 focus-within:ring-2 focus-within:ring-primary'} ${selected ? 'ring-2 ring-destructive' : ''}`}
      onClick={handleCardClick}
    >
      <div className="relative">
        <CameraThumbnail
          src={camera.latest_detection_thumbnail}
          alt={camera.name}
          totalDetections={camera.total_detections}
          totalPhotos={camera.total_photos}
          totalVideos={camera.total_videos}
          locked={camera.is_locked}
        />
        {/* Whole-card primary action = open Events. The card div is clickable
            for pointer users (handleCardClick); this button is the accessible
            keyboard-focusable, screen-reader-named control and the visible
            hover/focus affordance. It deliberately covers only the thumbnail,
            never the body, so the #049 stat/ID hover tooltips keep working —
            a full ::after card overlay would have suppressed them. */}
        {!selectionMode && (
          <button
            type="button"
            aria-label={t('card.viewEventsAria', { name: camera.name })}
            onClick={handleViewEvents}
            className="absolute inset-0 z-[5] flex items-center justify-center opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-hover:bg-black/30 focus:opacity-100 focus:bg-black/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary motion-reduce:transition-none"
          >
            <span className="rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm shadow-sm">
              {t('card.viewEvents')} →
            </span>
          </button>
        )}
        {/* Type pill — top-left. Hidden in selection mode (checkbox takes it). */}
        {!selectionMode && (
          <div
            className="absolute top-2 left-2 z-10 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <IngestionTypeBadge type={camera.ingestion_type} overlay />
          </div>
        )}
        {/* Online/Offline pill — top-right. Nothing for FTP (is_online null). */}
        <div
          className={`absolute top-2 right-2 z-10 ${selectionMode ? '' : 'cursor-default'}`}
          onClick={(e) => { if (!selectionMode) e.stopPropagation() }}
        >
          <OnlineStatusBadge isOnline={camera.is_online} lastSeen={camera.last_seen} overlay />
        </div>
        {/* Bottom-right cluster — battery hazard (≤25%, also shown in selection
            mode) + always-on expand → lightbox, the fixed corner anchor;
            battery slides in to its left when present. */}
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5">
          <BatteryAlertPill level={camera.battery_level} />
          {!selectionMode && camera.latest_detection_thumbnail && (
            <Tooltip content={t('card.openImageTooltip')}>
              <button
                type="button"
                aria-label={t('card.openImageTooltip')}
                onClick={(e) => { e.stopPropagation(); onThumbnailClick(camera) }}
                className="flex items-center justify-center rounded-full bg-black/55 p-1.5 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-black/75"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </Tooltip>
          )}
        </div>
        {/* Selection checkbox overlay */}
        {selectionMode && (
          <div className="absolute top-2 left-2 z-10">
            {selected
              ? <CheckSquare className="h-5 w-5 text-destructive drop-shadow" />
              : <Square className="h-5 w-5 text-white drop-shadow" />
            }
          </div>
        )}
      </div>

      {/* Identity — name, group, location */}
      <CardHeader className="pb-1.5 pt-2 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <CardTitle className="text-base truncate leading-tight">{camera.name}</CardTitle>
              {!camera.is_owner && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary border border-primary/20 shrink-0">
                  <Users className="h-2.5 w-2.5" />
                  {tc('badge.shared')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {camera.group_name && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <FolderOpen className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[100px]">{camera.group_name}</span>
                </span>
              )}
              {camera.location && (
                <CardDescription className="truncate text-xs">
                  {camera.location}
                </CardDescription>
              )}
              {!camera.group_name && !camera.location && (
                <CardDescription className="text-xs">{t('card.noLocationSet')}</CardDescription>
              )}
            </div>
          </div>

          {/* Actions menu — hidden in selection mode */}
          {!selectionMode && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0 h-7 w-7"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  onClick={(e) => { e.stopPropagation(); onDeleteRequest(camera) }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('card.deleteCamera')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-2 px-4 pb-4 flex-1 flex flex-col">

        {/* Telemetry chip strip (#049) — Battery / Signal / Temp / SD / AI as
            icon+value chips. Battery & Signal reuse their indicator components
            unchanged so severity colours (green/amber/red) are preserved. The
            text labels are dropped; each chip carries a Tooltip + aria-label
            with the full label+value for a11y. Firmware relocated to the
            Hardware IDs block below. Wraps on narrow xl 4-col cards — still far
            less vertical space than the two labelled grids it replaces. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Tooltip content={`${t('stats.battery')}${camera.battery_level === null ? '' : ` ${camera.battery_level}%`}`}>
            <span aria-label={`${t('stats.battery')}${camera.battery_level === null ? '' : ` ${camera.battery_level}%`}`}>
              <BatteryIndicator level={camera.battery_level} />
            </span>
          </Tooltip>
          <Tooltip content={`${t('stats.signal')}${camera.signal_level === null ? '' : ` ${camera.signal_level}/5`}`}>
            <span aria-label={`${t('stats.signal')}${camera.signal_level === null ? '' : ` ${camera.signal_level}/5`}`}>
              <SignalIndicator level={camera.signal_level} />
            </span>
          </Tooltip>
          <Tooltip content={`${t('stats.temperature')}${camera.temperature === null ? '' : ` ${camera.temperature}°C`}`}>
            <span
              aria-label={`${t('stats.temperature')}${camera.temperature === null ? '' : ` ${camera.temperature}°C`}`}
              className="flex items-center gap-1.5 text-sm font-medium"
            >
              <Thermometer className="h-4 w-4 text-muted-foreground" />
              {camera.temperature === null
                ? <span className="text-muted-foreground/50">—</span>
                : `${camera.temperature}°C`}
            </span>
          </Tooltip>
          <Tooltip content={`${t('stats.sdCard')} ${formatSDCard(camera.sd_used_mb, camera.sd_total_mb) || '—'}`}>
            <span
              aria-label={`${t('stats.sdCard')} ${formatSDCard(camera.sd_used_mb, camera.sd_total_mb) || '—'}`}
              className="flex items-center gap-1.5 text-sm font-medium"
            >
              <HardDrive className="h-4 w-4 text-muted-foreground" />
              {formatSDCard(camera.sd_used_mb, camera.sd_total_mb)
                ? formatSDCard(camera.sd_used_mb, camera.sd_total_mb)
                : <span className="text-muted-foreground/50">—</span>}
            </span>
          </Tooltip>
          <Tooltip content={`${t('stats.aiModels')}: ${camera.ai_models?.length || 0} ${t('stats.active')}`}>
            <span
              aria-label={`${t('stats.aiModels')}: ${camera.ai_models?.length || 0} ${t('stats.active')}`}
              className="flex items-center gap-1.5 text-sm font-medium"
            >
              <Cpu className="h-4 w-4 text-muted-foreground" />
              {camera.ai_models?.length || 0}
            </span>
          </Tooltip>
        </div>

        {/* Hardware IDs — IMEI, IMSI/Network or ICCID, + Firmware (#049,
            relocated here from the old stats grid as reference data). */}
        <div className="rounded-md bg-muted/50 px-2.5 py-1.5 space-y-1">
          <div className="flex items-center justify-between gap-2 min-w-0">
            <span className="text-muted-foreground text-[10px] uppercase tracking-wide shrink-0">{t('card.imei')}</span>
            <div className="flex items-center justify-end gap-1 min-w-0">
              <p className="font-mono text-xs truncate text-foreground/80 text-right">{camera.imei}</p>
              <CopyButton value={camera.imei} label={t('card.imei')} />
            </div>
          </div>
          {camera.ingestion_type === 'http' ? (
            <>
              <div className="flex items-center justify-between gap-2 min-w-0">
                <span className="text-muted-foreground text-[10px] uppercase tracking-wide shrink-0">{t('card.imsi')}</span>
                <div className="flex items-center justify-end gap-1 min-w-0">
                  <p className="font-mono text-xs truncate text-foreground/80 text-right">
                    {camera.imsi || <span className="text-muted-foreground/40">—</span>}
                  </p>
                  <CopyButton value={camera.imsi} label={t('card.imsi')} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 min-w-0">
                <span className="text-muted-foreground text-[10px] uppercase tracking-wide shrink-0">{t('card.network')}</span>
                <p className="font-mono text-xs truncate text-foreground/80 text-right">
                  {camera.network_carrier || <span className="text-muted-foreground/40">—</span>}
                </p>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between gap-2 min-w-0">
              <span className="text-muted-foreground text-[10px] uppercase tracking-wide shrink-0">{t('card.iccid')}</span>
              <div className="flex items-center justify-end gap-1 min-w-0">
                <p className="font-mono text-xs truncate text-foreground/80 text-right">
                  {camera.iccid || <span className="text-muted-foreground/40">—</span>}
                </p>
                <CopyButton value={camera.iccid} label={t('card.iccid')} />
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 min-w-0">
            <span className="text-muted-foreground text-[10px] uppercase tracking-wide shrink-0">{t('stats.firmware')}</span>
            <div className="flex items-center justify-end gap-1 min-w-0">
              <Tooltip content={camera.firmware_version || ''}>
                <p className="font-mono text-xs truncate text-foreground/80 text-right">
                  {camera.firmware_version || <span className="text-muted-foreground/40">—</span>}
                </p>
              </Tooltip>
              <CopyButton value={camera.firmware_version} label={t('stats.firmware')} />
            </div>
          </div>
        </div>

        {/* Recency (compact, #050.1) — last contact + last detection on ONE
            row, middot-separated. Text shrunk via formatRelativeTimeShort so
            this no longer risks wrap (the #049 reason for splitting them is
            gone). No label words: the Activity (contact) / Eye (detection)
            icons differentiate; full labels stay in the tooltip + aria-label.
            mt-auto anchors the recency+actions pair to the card bottom. */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-auto">
          <Tooltip content={t('card.lastContactTooltip')}>
            <span
              className="flex min-w-0 items-center gap-1"
              aria-label={camera.last_seen
                ? t('card.lastContact', { time: formatRelativeTimeShort(camera.last_seen) })
                : t('card.neverContacted')}
            >
              <Activity className="h-3 w-3 shrink-0" />
              {camera.last_seen
                ? <span className="truncate">{formatRelativeTimeShort(camera.last_seen) === 'now'
                    ? t('card.justNow')
                    : t('card.timeAgo', { time: formatRelativeTimeShort(camera.last_seen) })}</span>
                : <span className="truncate text-muted-foreground/50">{t('card.neverContacted')}</span>}
            </span>
          </Tooltip>
          <span aria-hidden="true" className="text-muted-foreground/30">·</span>
          <Tooltip content={t('card.lastDetectionTooltip')}>
            <span
              className="flex min-w-0 items-center gap-1"
              aria-label={camera.last_detection_timestamp
                ? t('card.lastDetection', { time: formatRelativeTimeShort(camera.last_detection_timestamp) })
                : t('card.noDetectionsYet')}
            >
              <Eye className="h-3 w-3 shrink-0" />
              {camera.last_detection_timestamp
                ? <span className="truncate">{formatRelativeTimeShort(camera.last_detection_timestamp) === 'now'
                    ? t('card.justNow')
                    : t('card.timeAgo', { time: formatRelativeTimeShort(camera.last_detection_timestamp) })}</span>
                : <span className="truncate text-muted-foreground/50">{t('card.noDetectionsYet')}</span>}
            </span>
          </Tooltip>
        </div>

        {/* Dedicated actions row (#050.1) — controls on their own row, not
            mixed into the info above; eliminates the xl collision risk
            entirely. Right-aligned; raised z-10; each button stopPropagation's
            so it doesn't trigger the card's open-Events click. Hidden in
            selection mode. */}
        {!selectionMode && (
          <div className="relative z-10 flex items-center gap-1">
            {/* #052 — settings status + when-applied, left of the action icons
                (mr-auto pushes the icons to the right edge). */}
            <div className="mr-auto flex items-center min-w-0 max-w-[60%] overflow-hidden">
              {isSuperuser && camera.sim_state === 'mismatched' && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400 border border-red-500/20"
                  title="Reported SIM IMSI differs from registered — alerts suppressed (#040)"
                >
                  SIM mismatch
                </span>
              )}
              {camera.is_locked && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 dark:text-red-400 border border-red-500/30"
                  title="SIM mismatch — camera locked until reviewed by an administrator"
                >
                  <Lock className="h-3 w-3 shrink-0" /> SIM mismatch — locked
                </span>
              )}
              {/* #061 — show absolute datetime (was relative "2m ago"). Tooltip
                  on hover keeps the full ISO for precision. */}
              {camera.settings_state === 'applied' && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                  title={camera.settings_applied_at ?? undefined}
                >
                  {t('card.settingsApplied')}
                  {camera.settings_applied_at && (
                    <span className="text-emerald-600/70 dark:text-emerald-400/70">
                      {'·'} {formatAbsoluteDateTime(camera.settings_applied_at)}
                    </span>
                  )}
                </span>
              )}
              {camera.settings_state === 'pending' && (
                <span
                  className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20"
                  title={camera.settings_pending_since ?? undefined}
                >
                  {t('card.settingsPending')}
                  {camera.settings_pending_since && (
                    <span className="text-amber-600/70 dark:text-amber-400/70">
                      {'·'} {formatAbsoluteDateTime(camera.settings_pending_since)}
                    </span>
                  )}
                </span>
              )}
            </div>
            {camera.iccid && (
              <Tooltip content={t('card.simDetails')}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 flex-shrink-0 text-muted-foreground hover:text-purple-500 gap-1 px-1.5"
                  onClick={(e) => { e.stopPropagation(); onSimRequest(camera) }}
                >
                  <Signal className="h-4 w-4" />
                  {simUsageMB != null && (
                    <span className="text-[10px] font-medium">{simUsageMB >= 1024 ? `${(simUsageMB / 1024).toFixed(1)} GB` : `${Math.round(simUsageMB)} MB`}</span>
                  )}
                </Button>
              </Tooltip>
            )}
            {camera.latitude && camera.longitude && (
              <Tooltip content={t('card.viewOnMap')}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-emerald-500"
                  onClick={(e) => { e.stopPropagation(); onMapRequest(camera) }}
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </Tooltip>
            )}
            <Tooltip content={t('card.cameraSettings')}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onEditRequest(camera) }}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </Tooltip>
          </div>
        )}
      </CardContent>
    </Card>
  )
})

// Loading skeleton — matches actual CameraCard structure
function CameraCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <div className="h-48 shimmer" />
      <CardHeader className="pb-1.5 pt-2 px-4">
        <div className="shimmer h-5 w-32 rounded" />
        <div className="shimmer h-3.5 w-24 rounded mt-1" />
      </CardHeader>
      <CardContent className="space-y-2 px-4 pb-4">
        <div className="shimmer h-5 rounded" />
        <div className="shimmer h-16 rounded" />
        <div className="shimmer h-3 w-44 rounded" />
        <div className="shimmer h-7 w-24 rounded self-end" />
      </CardContent>
    </Card>
  )
}

type GroupFilterValue = 'all' | 'ungrouped' | number
type IngestionFilterValue = 'all' | 'http' | 'ftp'
type OnlineFilterValue = 'all' | 'online' | 'offline' | 'unknown'

function initialGroupFilter(search: string): GroupFilterValue {
  const params = new URLSearchParams(search)
  const group = params.get('group')
  if (!group || group === 'all') return 'all'
  if (group === 'ungrouped') return 'ungrouped'
  const n = Number(group)
  return Number.isFinite(n) && n > 0 ? n : 'all'
}

function initialIngestionFilter(search: string): IngestionFilterValue {
  const v = new URLSearchParams(search).get('ingestion')
  return v === 'http' || v === 'ftp' ? v : 'all'
}

function initialOnlineFilter(search: string): OnlineFilterValue {
  const v = new URLSearchParams(search).get('online')
  return v === 'online' || v === 'offline' || v === 'unknown' ? v : 'all'
}

function initialBatteryLowFilter(search: string): boolean {
  return new URLSearchParams(search).get('battery_low') === 'true'
}

// Click-to-edit group row for manage groups modal
function GroupRow({
  group,
  onRename,
  onDelete,
  renaming,
  deleting,
}: {
  group: { id: number; name: string; camera_count: number }
  onRename: (name: string) => void
  onDelete: () => void
  renaming: boolean
  deleting: boolean
}) {
  const { t: tc } = useTranslation('common')
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(group.name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const handleSave = () => {
    const value = editValue.trim()
    if (value && value !== group.name) {
      onRename(value)
    } else {
      setEditValue(group.name)
    }
    setEditing(false)
  }

  return (
    <div className="flex items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors group/row">
      <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />

      {editing ? (
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <TextInput
            ref={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave()
              if (e.key === 'Escape') { setEditValue(group.name); setEditing(false) }
            }}
            className="h-8 text-sm flex-1"
            disabled={renaming}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 flex-shrink-0"
            onClick={handleSave}
            disabled={renaming}
          >
            {renaming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={() => { setEditValue(group.name); setEditing(false) }}
            disabled={renaming}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <>
          <p className="text-sm flex-1 min-w-0 truncate">{group.name}</p>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground flex-shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity"
            onClick={() => { setEditValue(group.name); setEditing(true) }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        </>
      )}

      <span className="text-[11px] text-muted-foreground whitespace-nowrap rounded-full bg-muted px-2 py-0.5">
        {tc('camera', { count: group.camera_count })}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
        onClick={onDelete}
        disabled={deleting}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  )
}

export function CamerasPage() {
  const { t } = useTranslation('cameras')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()
  const location = useLocation()
  const direction = useDirectionStore((s) => s.direction)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 300)

  // Group filter state — seeded from ?group= URL param on first mount
  const [groupFilter, setGroupFilter] = useState<GroupFilterValue>(() =>
    initialGroupFilter(location.search)
  )

  // #034 filters — seeded from ?ingestion=, ?online=, ?battery_low= on first mount
  const [ingestionFilter, setIngestionFilter] = useState<IngestionFilterValue>(() =>
    initialIngestionFilter(location.search)
  )
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilterValue>(() =>
    initialOnlineFilter(location.search)
  )
  const [batteryLowFilter, setBatteryLowFilter] = useState<boolean>(() =>
    initialBatteryLowFilter(location.search)
  )

  // Bulk assign dialog state
  const [bulkGroupDialogOpen, setBulkGroupDialogOpen] = useState(false)
  const [bulkGroupId, setBulkGroupId] = useState<number | null>(null)

  // Manage groups dialog state
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')

  // SIM details modal state
  const [simCamera, setSimCamera] = useState<CameraType | null>(null)

  // Camera groups
  const { data: cameraGroups = [] } = useQuery({
    queryKey: queryKeys.cameraGroups.all(),
    queryFn: () => cameraGroupsApi.getAll(),
    ...queryOptions.cameraGroups.all,
  })

  // Bulk SIM usage — single cached API call for all cameras
  const { data: simUsageData } = useQuery({
    queryKey: ['sim-usage'],
    queryFn: () => camerasApi.getSimUsage(),
    staleTime: 15 * 60 * 1000, // 15 min client-side cache
  })
  const simUsageMap: Record<string, SIMUsage> = simUsageData?.usage ?? {}

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [selectModeActive, setSelectModeActive] = useState(false)
  const selectionMode = selectModeActive || selectedIds.size > 0

  // Edit sheet state
  const [editCamera, setEditCamera] = useState<CameraType | null>(null)
  // Map modal state
  const [mapCamera, setMapCamera] = useState<CameraType | null>(null)
  // Add camera modal state
  const [addModalOpen, setAddModalOpen] = useState(false)
  // Thumbnail lightbox state — opens EventDetailModal with the camera's latest event
  const [lightboxEvent, setLightboxEvent] = useState<EventType | null>(null)
  const handleThumbnailClick = useCallback(async (camera: CameraType) => {
    if (camera.is_locked) {  // #077 — SIM mismatch: locked, no event access
      toast.error('Camera locked', { description: 'SIM mismatch — under review. Please contact support.' })
      return
    }
    if (!camera.latest_detection_thumbnail) return
    try {
      const response = await eventsApi.getAll({ camera: camera.id })
      const latest = response.results[0]
      if (latest) {
        setLightboxEvent(latest)
      } else {
        toast.error(t('messages.lightboxNoEvent'))
      }
    } catch {
      toast.error(t('messages.lightboxFailed'))
    }
  }, [t])

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    title: string
    description: string
    onConfirm: () => void
  }>({ open: false, title: '', description: '', onConfirm: () => {} })

  // Infinite scroll cameras — server-side filtered by search + group
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: queryKeys.cameras.infinite(
      debouncedSearch,
      String(groupFilter),
      ingestionFilter,
      onlineFilter,
      String(batteryLowFilter),
    ),
    queryFn: ({ pageParam = 1 }) =>
      camerasApi.getInfinite(
        pageParam as number,
        debouncedSearch || undefined,
        groupFilter !== 'all' ? String(groupFilter) : undefined,
        ingestionFilter !== 'all' ? ingestionFilter : undefined,
        onlineFilter !== 'all' ? onlineFilter : undefined,
        batteryLowFilter,
      ),
    getNextPageParam: (lastPage: PaginatedResponse<CameraType>, allPages: PaginatedResponse<CameraType>[]) =>
      lastPage.next ? allPages.length + 1 : undefined,
    initialPageParam: 1,
    ...queryOptions.cameras.infinite,
  })

  const cameras = data?.pages.flatMap((p: PaginatedResponse<CameraType>) => p.results) ?? []
  const totalCount = data?.pages[0]?.count ?? 0

  const selectedCameras = cameras.filter((camera) => selectedIds.has(camera.id))
  const selectedHaveGroup = selectedCameras.some((camera) => camera.group_id !== null)

  // IntersectionObserver sentinel for infinite scroll
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const element = loadMoreRef.current
    if (!element) return
    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
      rootMargin: '100px',
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [handleObserver])

  // Clear selection when search or any filter changes
  useEffect(() => {
    setSelectedIds(new Set())
  }, [debouncedSearch, groupFilter, ingestionFilter, onlineFilter, batteryLowFilter])

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => camerasApi.delete(id))),
    onSuccess: (_, ids) => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      setSelectedIds(new Set())
      setConfirmDialog((d) => ({ ...d, open: false }))
      toast.success(t('messages.deleted', { count: ids.length }))
    },
    onError: () => {
      toast.error(t('messages.deleteFailed'))
    },
  })

  // Bulk assign to group mutation
  const bulkAssignMutation = useMutation({
    mutationFn: ({ groupId, cameraIds }: { groupId: number; cameraIds: number[] }) =>
      cameraGroupsApi.addCameras(groupId, cameraIds),
    onSuccess: (_data, variables) => {
      const { cameraIds } = variables
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      setSelectedIds(new Set())
      setSelectModeActive(false)
      setBulkGroupDialogOpen(false)
      toast.success(t('messages.assigned', { count: cameraIds.length }))
    },
    onError: () => {
      toast.error(t('messages.assignFailed'))
    },
  })

  // Bulk remove from groups mutation
  const bulkRemoveMutation = useMutation({
    mutationFn: async (cameraIds: number[]) => {
      const camerasById = new Map<number, CameraType>()
      cameras.forEach((camera) => {
        camerasById.set(camera.id, camera)
      })

      const groupToCameraIds = new Map<number, number[]>()
      cameraIds.forEach((id) => {
        const camera = camerasById.get(id)
        if (camera && camera.group_id) {
          const list = groupToCameraIds.get(camera.group_id) ?? []
          list.push(id)
          groupToCameraIds.set(camera.group_id, list)
        }
      })

      const ops: Promise<unknown>[] = []
      groupToCameraIds.forEach((ids, groupId) => {
        ops.push(cameraGroupsApi.removeCameras(groupId, ids))
      })

      if (ops.length) {
        await Promise.all(ops)
      }
    },
    onSuccess: (_data, cameraIds) => {
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      setSelectedIds(new Set())
      toast.success(t('messages.unlinked', { count: cameraIds.length }))
    },
    onError: () => {
      toast.error(t('messages.unlinkFailed'))
    },
  })

  // Manage groups mutations
  const createGroupMutation = useMutation({
    mutationFn: (name: string) => cameraGroupsApi.create({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      setNewGroupName('')
      toast.success(t('groups.created'))
    },
    onError: () => {
      toast.error(t('groups.createFailed'))
    },
  })

  const renameGroupMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      cameraGroupsApi.update(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      toast.success(t('groups.renamed'))
    },
    onError: () => {
      toast.error(t('groups.renameFailed'))
    },
  })

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => cameraGroupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.cameraGroups.all() })
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      toast.success(t('groups.deleted'))
    },
    onError: () => {
      toast.error(t('groups.deleteFailed'))
    },
  })

  // Selection helpers
  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedIds(new Set(cameras.map((c) => c.id)))
  const clearSelection = () => { setSelectedIds(new Set()); setSelectModeActive(false) }

  // Open confirm for single delete
  const requestDeleteSingle = (camera: CameraType) => {
    setConfirmDialog({
      open: true,
      title: t('deleteConfirm.title', { name: camera.name }),
      description: t('deleteConfirm.description'),
      onConfirm: () => deleteMutation.mutate([camera.id]),
    })
  }

  // Open confirm for bulk delete
  const requestDeleteSelected = () => {
    const count = selectedIds.size
    setConfirmDialog({
      open: true,
      title: t('deleteConfirm.titleBulk', { count }),
      description: t('deleteConfirm.descriptionBulk', { count }),
      onConfirm: () => deleteMutation.mutate(Array.from(selectedIds)),
    })
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-up">
        <div>
          <div className="flex items-center gap-2">
            {typeof groupFilter === 'number' || groupFilter === 'ungrouped' ? (
              <button
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => { setGroupFilter('all') }}
              >
                {tc('nav.cameras')}
              </button>
            ) : (
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{tc('nav.cameras')}</h2>
            )}
            {(typeof groupFilter === 'number' || groupFilter === 'ungrouped') && (
              <>
                <span className="text-muted-foreground/40">/</span>
                <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                  {groupFilter === 'ungrouped'
                    ? t('filter.ungrouped')
                    : (cameraGroups.find((g) => g.id === groupFilter)?.name ?? 'Group')
                  }
                </h2>
              </>
            )}
          </div>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('page.description')}
            {totalCount > 0 && ` (${totalCount} ${tc('total')})`}
          </p>
        </div>
        <Button className="w-full sm:w-auto" onClick={() => setAddModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('page.addCamera')}
        </Button>
      </div>

      {/* Search + Bulk Actions bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t('search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {debouncedSearch && (
          <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
            {tc('actions.clear')}
          </Button>
        )}

        {/* Group filter */}
        <div className="w-full sm:w-44">
          <Select
            value={String(groupFilter)}
            onValueChange={(value) => {
              if (value === 'all' || value === 'ungrouped') {
                setGroupFilter(value)
              } else {
                setGroupFilter(Number(value))
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('filter.byGroup')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filter.allCameras')}</SelectItem>
              <SelectItem value="ungrouped">{t('filter.ungrouped')}</SelectItem>
              {cameraGroups.map((group) => (
                <SelectItem key={group.id} value={String(group.id)}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* #034: Ingestion type filter */}
        <div className="w-full sm:w-32">
          <Select
            value={ingestionFilter}
            onValueChange={(value) => setIngestionFilter(value as IngestionFilterValue)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="http">HTTP</SelectItem>
              <SelectItem value="ftp">FTP</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* #034: Online status filter */}
        <div className="w-full sm:w-36">
          <Select
            value={onlineFilter}
            onValueChange={(value) => setOnlineFilter(value as OnlineFilterValue)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="online">Online</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
              <SelectItem value="unknown">Unknown (FTP)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* #034: Battery low toggle */}
        <Button
          variant={batteryLowFilter ? 'default' : 'outline'}
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => setBatteryLowFilter((v) => !v)}
          title="Show only cameras with battery below 25%"
        >
          Low battery
        </Button>

        {/* Show "Add cameras to group" when viewing a specific group */}
        {typeof groupFilter === 'number' && (
          <Button
            variant="default"
            size="sm"
            className="w-full sm:w-auto gap-1.5"
            onClick={() => {
              setBulkGroupId(groupFilter)
              setGroupFilter('all')
              setSelectModeActive(true)
              setSelectedIds(new Set())
              toast.success(t('page.selectCamerasHint'))
            }}
          >
            <Plus className="h-3.5 w-3.5" />
            {t('page.addCamerasToGroup')}
          </Button>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          onClick={() => setManageGroupsOpen(true)}
        >
          {t('page.manageGroups')}
        </Button>

        {/* Bulk action toolbar — appears when items are selected */}
        {selectionMode && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-muted-foreground">
              {t('bulk.selected', { count: selectedIds.size })}
            </span>
            <Button variant="ghost" size="sm" onClick={selectAll}>
              {t('page.selectAllLoaded', { count: cameras.length })}
            </Button>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {tc('actions.clear')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!cameraGroups.length) {
                  toast.error(t('groups.noGroupsCreate'))
                  return
                }
                if (!bulkGroupId) {
                  setBulkGroupId(cameraGroups[0]?.id ?? null)
                }
                setBulkGroupDialogOpen(true)
              }}
              disabled={bulkAssignMutation.isPending || cameraGroups.length === 0}
            >
              {t('page.assignToGroup')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => bulkRemoveMutation.mutate(Array.from(selectedIds))}
              disabled={bulkRemoveMutation.isPending || !selectedHaveGroup}
            >
              {t('page.removeFromGroup')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={requestDeleteSelected}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('bulk.delete', { count: selectedIds.size })}
            </Button>
          </div>
        )}
      </div>

      {/* Error State */}
      {isError && (
        <Card className="border-destructive">
          <CardContent className="flex h-32 items-center justify-center text-destructive">
            {t('errors.loadingError', { message: (error as Error)?.message || '' })}
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(6)].map((_, i) => (
            <CameraCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && cameras.length === 0 && (
        <Card>
          <CardContent className="flex h-64 flex-col items-center justify-center text-center">
            <Camera className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="text-lg font-semibold">{t('page.noCamerasFound')}</h3>
            <p className="text-sm text-muted-foreground">
              {debouncedSearch
                ? t('page.tryDifferentSearch')
                : t('page.getStarted')}
            </p>
            {!debouncedSearch && (
              <Button className="mt-4" onClick={() => setAddModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('page.addCamera')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Camera Grid / Board / List — direction switch around item rendering only.
          The sentinel (loadMoreRef) stays OUTSIDE the switch so infinite scroll
          works identically in every direction. current = verbatim upstream grid. */}
      {!isLoading && cameras.length > 0 && (
        <>
          {/* ── current: verbatim upstream grid ─────────────────────────── */}
          {direction === 'current' && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {cameras.map((camera: CameraType) => (
                <CameraCard
                  key={camera.id}
                  camera={camera}
                  selected={selectedIds.has(camera.id)}
                  selectionMode={selectionMode}
                  onToggleSelect={toggleSelect}
                  onDeleteRequest={requestDeleteSingle}
                  onEditRequest={setEditCamera}
                  onMapRequest={setMapCamera}
                  onSimRequest={setSimCamera}
                  onThumbnailClick={handleThumbnailClick}
                  simUsageMB={camera.iccid && simUsageMap[camera.iccid] ? simUsageMap[camera.iccid].monthUsedMB : null}
                />
              ))}
            </div>
          )}

          {/* ── command: status board rows (§1) ─────────────────────────── */}
          {direction === 'command' && (
            <CommandCameraBoard
              cameras={cameras}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              simUsageMap={simUsageMap}
              onToggleSelect={toggleSelect}
              onDeleteRequest={requestDeleteSingle}
              onEditRequest={setEditCamera}
              onMapRequest={setMapCamera}
              onSimRequest={setSimCamera}
            />
          )}

          {/* ── saas: dense list rows (§2) ───────────────────────────────── */}
          {direction === 'saas' && (
            <SaasCameraList
              cameras={cameras}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              simUsageMap={simUsageMap}
              onToggleSelect={toggleSelect}
              onDeleteRequest={requestDeleteSingle}
              onEditRequest={setEditCamera}
              onMapRequest={setMapCamera}
              onSimRequest={setSimCamera}
            />
          )}

          {/* ── expressive: photo-hero cards (§3) ───────────────────────── */}
          {direction === 'expressive' && (
            <ExpressiveCameraCards
              cameras={cameras}
              selectionMode={selectionMode}
              selectedIds={selectedIds}
              simUsageMap={simUsageMap}
              onToggleSelect={toggleSelect}
              onDeleteRequest={requestDeleteSingle}
              onEditRequest={setEditCamera}
              onMapRequest={setMapCamera}
              onSimRequest={setSimCamera}
            />
          )}

          {/* Load More Trigger — OUTSIDE the direction switch so the observer
              attaches identically regardless of which renderer is active */}
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">{t('page.loadingMore')}</span>
              </div>
            )}
            {!hasNextPage && (
              <p className="text-sm text-muted-foreground">
                {t('page.showingAll', { count: cameras.length })}
                {totalCount > cameras.length && ` (${totalCount} total)`}
              </p>
            )}
          </div>
        </>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog((d) => ({ ...d, open }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={tc('actions.delete')}
        loading={deleteMutation.isPending}
        onConfirm={confirmDialog.onConfirm}
      />

      {/* Camera Edit Sheet */}
      {editCamera && (
        <CameraEditSheet
          camera={editCamera}
          open={!!editCamera}
          onOpenChange={(open) => { if (!open) setEditCamera(null) }}
        />
      )}

      {/* Camera Map Modal */}
      <CameraMapModal
        camera={mapCamera}
        open={!!mapCamera}
        onOpenChange={(open) => { if (!open) setMapCamera(null) }}
      />

      {/* Add Camera Modal */}
      <AddCameraModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
      />

      {/* Latest-detection lightbox — single-event reuse of EventDetailModal */}
      <EventDetailModal
        events={lightboxEvent ? [lightboxEvent] : []}
        selectedIndex={lightboxEvent ? 0 : null}
        onClose={() => setLightboxEvent(null)}
        onNavigate={() => {}}
      />

      {/* SIM Detail Modal */}
      {simCamera && (
        <SIMDetailModal
          open={!!simCamera}
          onOpenChange={(open) => { if (!open) setSimCamera(null) }}
          cameraId={simCamera.id}
          cameraName={simCamera.name}
          iccid={simCamera.iccid || ''}
          cachedUsage={simCamera.iccid ? simUsageMap[simCamera.iccid] ?? null : null}
        />
      )}

      {/* Bulk Assign to Group Dialog */}
      <Dialog.Root open={bulkGroupDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setBulkGroupDialogOpen(false)
        } else {
          if (!bulkGroupId && cameraGroups.length) {
            setBulkGroupId(cameraGroups[0].id)
          }
        }
      }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>
            {/* Header */}
            <div className="px-5 py-4 border-b border-border/60">
              <Dialog.Title className="text-sm font-semibold">
                {t('bulk.assignTitle', { count: selectedIds.size })}
              </Dialog.Title>
              <Dialog.Description className="text-xs text-muted-foreground mt-1">
                {t('bulk.assignDescription')}
              </Dialog.Description>
            </div>

            {/* Body */}
            <div className="px-5 py-4">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {t('edit.group')}
              </label>
              <Select
                value={bulkGroupId === null ? undefined : String(bulkGroupId)}
                onValueChange={(value) => setBulkGroupId(Number(value))}
                disabled={bulkAssignMutation.isPending || cameraGroups.length === 0}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder={cameraGroups.length ? t('edit.selectGroup') : t('edit.noGroups')} />
                </SelectTrigger>
                <SelectContent>
                  {cameraGroups.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-border/60 bg-muted/20">
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs"
                onClick={() => setBulkGroupDialogOpen(false)}
                disabled={bulkAssignMutation.isPending}
              >
                {tc('actions.cancel')}
              </Button>
              <Button
                size="sm"
                className="h-9 text-xs gap-1.5"
                onClick={() => {
                  if (!bulkGroupId) {
                    toast.error(t('groups.selectGroup'))
                    return
                  }
                  bulkAssignMutation.mutate({
                    groupId: bulkGroupId,
                    cameraIds: Array.from(selectedIds),
                  })
                }}
                disabled={bulkAssignMutation.isPending || !bulkGroupId}
              >
                {bulkAssignMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {tc('actions.assign')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Manage Groups Dialog */}
      <Dialog.Root open={manageGroupsOpen} onOpenChange={(open) => setManageGroupsOpen(open)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" style={{ animationDuration: '200ms' }} />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background shadow-2xl overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:zoom-in-[0.98] data-[state=closed]:zoom-out-[0.98] data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1" style={{ animationDuration: '250ms', animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)' }}>

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/60">
              <div>
                <Dialog.Title className="text-sm font-semibold">
                  {t('manageGroups.title')}
                </Dialog.Title>
                <Dialog.Description className="text-xs text-muted-foreground mt-0.5">
                  {t('manageGroups.description')}
                </Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>

            {/* Create new group */}
            <div className="px-5 py-4 border-b border-border/60 bg-muted/20">
              <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                {t('manageGroups.createNew')}
              </label>
              <div className="flex gap-2">
                <TextInput
                  placeholder={t('manageGroups.groupNamePlaceholder')}
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  disabled={createGroupMutation.isPending}
                  className="h-9 text-sm flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = newGroupName.trim()
                      if (value) createGroupMutation.mutate(value)
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="h-9 text-xs gap-1.5"
                  onClick={() => {
                    const value = newGroupName.trim()
                    if (!value) {
                      toast.error(t('groups.nameRequired'))
                      return
                    }
                    createGroupMutation.mutate(value)
                  }}
                  disabled={createGroupMutation.isPending || !newGroupName.trim()}
                >
                  {createGroupMutation.isPending
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Plus className="h-3.5 w-3.5" />
                  }
                  {tc('actions.create')}
                </Button>
              </div>
            </div>

            {/* Existing groups list */}
            <div className="max-h-[50vh] overflow-y-auto">
              {cameraGroups.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                  <FolderOpen className="h-8 w-8 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">
                    {t('groups.noGroupsYet')}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border/60">
                  {cameraGroups.map((group) => (
                    <GroupRow
                      key={group.id}
                      group={group}
                      onRename={(name) => renameGroupMutation.mutate({ id: group.id, name })}
                      onDelete={() => deleteGroupMutation.mutate(group.id)}
                      renaming={renameGroupMutation.isPending}
                      deleting={deleteGroupMutation.isPending}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center px-5 py-3 border-t border-border/60 bg-muted/20">
              <p className="text-[11px] text-muted-foreground">
                {tc('camera', { count: cameraGroups.reduce((sum, g) => sum + g.camera_count, 0) })} · {cameraGroups.length} {cameraGroups.length === 1 ? t('manageGroups.group') : t('manageGroups.groups')}
              </p>
              <Dialog.Close asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  disabled={
                    createGroupMutation.isPending ||
                    renameGroupMutation.isPending ||
                    deleteGroupMutation.isPending
                  }
                >
                  {tc('actions.close')}
                </Button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
